import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { cajaAPI, ventasAPI, utilAPI, fmt, fmtFecha, fmtHora } from '../services/api'
import { useAuth } from '../context/AuthContext'
import Boleta from '../components/Boleta'
import toast from 'react-hot-toast'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

const MONTOS_RAPIDOS = [5000, 10000, 20000, 50000, 100000]
const MOTIVOS_INGRESO = ['Sencillo de monedas', 'Vuelto erróneo', 'Fondo de caja', 'Otro']
const MOTIVOS_EGRESO  = ['Retiro de efectivo', 'Pago proveedor', 'Gasto operacional', 'Otro']

const num = v => parseFloat(String(v ?? '').replace(',', '.')) || 0
const hoyStr = () => new Date().toLocaleDateString('es-CL')

// Hook chico: sabe si estamos en pantalla angosta (celular), sin librerías nuevas
function useEsMobil(breakpoint = 760) {
  const [esMobil, setEsMobil] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`)
    const handler = e => setEsMobil(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [breakpoint])
  return esMobil
}

export default function Caja() {
  const { usuario } = useAuth()
  const navigate = useNavigate()
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin'
  const esMobil = useEsMobil()

  const [turno, setTurno]       = useState(null)
  const [cajas, setCajas]       = useState([])
  const [resumen, setResumen]   = useState(null)
  const [cargando, setCargando] = useState(true)
  const [montoInicial, setMontoInicial] = useState('')
  const [fondoSugerido, setFondoSugerido] = useState(0)
  const [cajaId, setCajaId]     = useState('')
  const [tab, setTab]           = useState('movimientos')

  // Cajas de un día anterior que quedaron abiertas (ej: corte de luz)
  const [pendientes, setPendientes] = useState([])
  const [cargandoPend, setCargandoPend] = useState(false)

  const [movForm, setMovForm]   = useState({ tipo:'ingreso', concepto:'', monto:'' })
  const [motivoCustom, setMotivoCustom] = useState(false)

  // Cierre — lo que el vendedor ingresa
  const [efectivoContado, setEfectivoContado] = useState('')
  const [debitoReal, setDebitoReal]           = useState('')
  const [creditoReal, setCreditoReal]         = useState('')
  const [transferenciaReal, setTransferenciaReal] = useState('')
  const [fondoManana, setFondoManana]         = useState('')
  const [cerrado, setCerrado]                 = useState(false)

  const [fechaHist, setFechaHist]   = useState(new Date().toLocaleDateString('en-CA'))
  const [ventasHist, setVentasHist] = useState([])
  const [cargandoHist, setCargandoHist] = useState(false)

  // Reimpresión de boleta/documento (disponible para todos los roles)
  const [reimpresion, setReimpresion]     = useState(null)
  const [cargandoReimp, setCargandoReimp] = useState(null)
  const [generandoPdf, setGenerandoPdf]   = useState(false)

  const reimprimir = async (v) => {
    setCargandoReimp(v.id)
    try {
      const { data } = await ventasAPI.obtener(v.id)
      setReimpresion({
        documento: data.documento,
        items: (data.detalles || []).map(d => ({
          cantidad: parseFloat(d.cantidad),
          descripcion: d.descripcion,
          precio_unitario: parseFloat(d.precio_unitario),
        })),
        pago: { nombre: data.pagos?.[0]?.medio_pago || '—' },
        vuelto: 0,
      })
    } catch { toast.error('No se pudo cargar el documento') }
    finally { setCargandoReimp(null) }
  }

  const imprimirBoleta = () => window.print()

  const descargarPdfReimp = async () => {
    setGenerandoPdf(true)
    try {
      const elemento = document.getElementById('boleta-imprimir')
      const canvas = await html2canvas(elemento, { scale: 3, backgroundColor: '#ffffff' })
      const imgData = canvas.toDataURL('image/png')
      const anchoMm = 80
      const altoMm = (canvas.height * anchoMm) / canvas.width
      const pdf = new jsPDF({ unit: 'mm', format: [anchoMm, altoMm] })
      pdf.addImage(imgData, 'PNG', 0, 0, anchoMm, altoMm)
      const folio = reimpresion?.documento?.folio || reimpresion?.documento?.id?.slice(-6).toUpperCase()
      pdf.save(`boleta-${folio}.pdf`)
    } catch { toast.error('Error al generar el PDF') }
    finally { setGenerandoPdf(false) }
  }

  useEffect(() => {
    cargarEstado()
    cargarPendientes()
    utilAPI.cajas().then(r => { setCajas(r.data); if (r.data[0]) setCajaId(r.data[0].id) })
    cajaAPI.ultimoFondo().then(r => setFondoSugerido(num(r.data?.fondo))).catch(()=>{})
  }, [])

  const cargarPendientes = async () => {
    setCargandoPend(true)
    try {
      const { data } = await cajaAPI.pendientes()
      setPendientes(data || [])
    } catch {} finally { setCargandoPend(false) }
  }

  const [pendienteAbierto, setPendienteAbierto] = useState(null)
  const [formPend, setFormPend] = useState({ efectivo:'', debito:'', credito:'', transferencia:'', fondo:'' })
  const [guardandoPend, setGuardandoPend] = useState(false)

  const abrirCierrePendiente = (p) => {
    setPendienteAbierto(pendienteAbierto === p.id ? null : p.id)
    setFormPend({ efectivo:'', debito:'', credito:'', transferencia:'', fondo:'' })
  }

  const cerrarPendiente = async (p) => {
    if (formPend.efectivo === '') { toast.error('Ingresa el efectivo contado'); return }
    if (!window.confirm(`¿Confirmas el cierre de la caja del ${fmtFecha(p.apertura_en)}?`)) return
    setGuardandoPend(true)
    try {
      await cajaAPI.cerrar({
        turno_id: p.id,
        monto_final_real: num(formPend.efectivo),
        debito_real: formPend.debito === '' ? null : num(formPend.debito),
        credito_real: formPend.credito === '' ? null : num(formPend.credito),
        transferencia_real: formPend.transferencia === '' ? null : num(formPend.transferencia),
        fondo_siguiente: num(formPend.fondo),
      })
      toast.success('Caja anterior cerrada correctamente')
      setPendienteAbierto(null)
      cargarPendientes()
    } catch {} finally { setGuardandoPend(false) }
  }
  // (por ejemplo, anotar a mano las ventas de un corte de luz). Se lleva el
  // POS a apuntar a ESA caja específica, no a la de hoy.
  const venderEnPendiente = (p) => {
    const ok = window.confirm(
      `Vas a vender en la caja abierta el ${fmtFecha(p.apertura_en)} (NO en la de hoy).\n\n` +
      `Úsalo para registrar los productos que vendiste a mano durante ese día, ` +
      `para que el stock se descuente igual que si fuera en vivo.\n\n` +
      `Cuando vuelvas a "Caja", esto se revierte solo a la caja de hoy. ¿Continuar?`
    )
    if (!ok) return
    localStorage.setItem('pos_turno', p.id)
    navigate('/pos')
  }

  useEffect(() => { if (tab === 'historial') cargarHistorial() }, [tab, fechaHist])

  const cargarEstado = async () => {
    setCargando(true)
    try {
      const { data } = await cajaAPI.turnoActivo()
      setTurno(data)
      if (data) {
        localStorage.setItem('pos_turno', data.id)
        const res = await cajaAPI.resumen(data.id)
        setResumen(res.data)
        setTab('movimientos')
      } else {
        localStorage.removeItem('pos_turno')
        setTab('apertura')
      }
    } finally { setCargando(false) }
  }

  const cargarHistorial = async () => {
    setCargandoHist(true)
    try {
      const { data } = await ventasAPI.listar({ desde: fechaHist + 'T00:00:00', hasta: fechaHist + 'T23:59:59', limit: 200 })
      setVentasHist(data.data || [])
    } catch { toast.error('Error al cargar historial') }
    finally { setCargandoHist(false) }
  }

  const abrir = async () => {
    if (!cajaId) { toast.error('Selecciona una caja'); return }
    try {
      await cajaAPI.abrir({ caja_id: cajaId, monto_inicial: num(montoInicial) })
      toast.success('Caja abierta')
      setMontoInicial('')
      cargarEstado()
    } catch {}
  }

  const cerrar = async () => {
    if (efectivoContado === '') { toast.error('Ingresa el efectivo contado'); return }
    if (!window.confirm('¿Confirmas el cierre de caja? El turno quedará guardado.')) return
    try {
      const { data } = await cajaAPI.cerrar({
        turno_id: turno.id,
        monto_final_real: num(efectivoContado),
        debito_real:  debitoReal  === '' ? null : num(debitoReal),
        credito_real: creditoReal === '' ? null : num(creditoReal),
        transferencia_real: transferenciaReal === '' ? null : num(transferenciaReal),
        fondo_siguiente: num(fondoManana),
      })
      toast.success(data.mensaje || 'Cierre registrado')
      setCerrado(true)
      localStorage.removeItem('pos_turno')
      // limpiar estado tras un momento para mostrar la confirmación
      setTimeout(() => {
        setTurno(null); setResumen(null); setCerrado(false)
        setEfectivoContado(''); setDebitoReal(''); setCreditoReal(''); setTransferenciaReal(''); setFondoManana('')
        setTab('apertura')
      }, esAdmin ? 100 : 2500)
    } catch {}
  }

  const registrarMov = async () => {
    if (!movForm.concepto) { toast.error('Indica el motivo'); return }
    if (!movForm.monto)    { toast.error('Indica el monto'); return }
    try {
      await cajaAPI.registrarMovimiento({ ...movForm, turno_id: turno.id, monto: num(movForm.monto) })
      toast.success('Movimiento registrado')
      setMovForm({ tipo: movForm.tipo, concepto:'', monto:'' })
      setMotivoCustom(false)
      const res = await cajaAPI.resumen(turno.id)
      setResumen(res.data)
    } catch {}
  }

  // ── Cálculos ──
  const movs = resumen?.movimientos_manuales || []
  const saldoInicial  = num(turno?.monto_inicial)
  const pagos = resumen?.ventas_por_pago || []
  const totalVentas   = num(resumen?.totales?.total_ventas)

  const esEfectivo = v => v.tipo === 'efectivo' || v.medio_pago?.toLowerCase().includes('efectivo')
  const otrosMedios = pagos.filter(v => !esEfectivo(v))

  // Teórico: SOLO lo envía el backend si es admin (ver_arqueo)
  const efectivoTeorico = resumen?.efectivo_teorico
  const verArqueo = resumen?.ver_arqueo && esAdmin

  const contado = efectivoContado === '' ? null : num(efectivoContado)
  const difEfectivo = (verArqueo && contado !== null && efectivoTeorico != null) ? contado - efectivoTeorico : null
  const totalHist = ventasHist.filter(v=>v.estado!=='anulado').reduce((s,v)=>s+num(v.total),0)
  // Saldo positivo o cuadrado → verde · Falta dinero (negativo) → rojo
  const colorDif = d => d === null ? 'var(--color-text)' : d < 0 ? 'var(--color-danger)' : 'var(--color-success)'

  const PanelRegistro = () => (
    <div style={{fontSize:13}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
        <span style={{fontSize:14, fontWeight:600}}>$ Movimientos de dinero</span>
        <span style={{color:'var(--color-muted)', fontSize:12}}>El {hoyStr()}</span>
      </div>
      <div style={{fontSize:11, color:'var(--color-muted)', textTransform:'uppercase', letterSpacing:'.05em', borderBottom:'1px solid var(--color-border)', paddingBottom:6, marginBottom:8}}>Registro</div>
      <div style={{display:'flex', flexDirection:'column', gap:6}}>
        <div style={{display:'flex', justifyContent:'space-between'}}><span>Inicio de caja</span><span style={{fontWeight:600}}>{fmt(saldoInicial)}</span></div>
        {movs.map((m,i) => (
          <div key={i} style={{display:'flex', justifyContent:'space-between'}}>
            <span style={{color:'var(--color-muted)'}}>{m.concepto}</span>
            <span style={{fontWeight:600, color: m.tipo==='ingreso'?'var(--color-success)':'var(--color-danger)'}}>
              {m.tipo==='ingreso'?'+':'-'}{fmt(m.monto)}
            </span>
          </div>
        ))}
        {movs.length === 0 && <div style={{color:'var(--color-hint)', fontSize:12, fontStyle:'italic'}}>Aún no hay movimientos hoy</div>}
      </div>
    </div>
  )

  if (cargando) return <div className="page-loading"><div className="spinner"/><span>Cargando caja...</span></div>

  const abierta = !!turno

  const NavBtn = ({ id, children, disabled }) => (
    <button disabled={disabled} onClick={()=>!disabled && setTab(id)}
      className={tab===id ? 'btn btn-primary' : 'btn btn-secondary'}
      style={{
        justifyContent:'center', padding:'12px', opacity: disabled?0.4:1, cursor: disabled?'not-allowed':'pointer',
        whiteSpace: esMobil ? 'nowrap' : 'normal', flexShrink: esMobil ? 0 : 1,
      }}>
      {children}
    </button>
  )

  return (
    <div style={{padding: esMobil ? 12 : 24}}>
      <div style={{marginBottom:16}}>
        <div className="section-title" style={{marginBottom:4}}>Caja — {turno?.caja_nombre || 'Caja 1'}</div>
        {abierta
          ? <span className="caja-status caja-abierta">● Abierta desde {fmtHora(turno.apertura_en)}</span>
          : <span className="caja-status caja-cerrada">● Caja cerrada</span>}
      </div>

      {/* ═══ CAJAS PENDIENTES DE UN DÍA ANTERIOR (ej: corte de luz) ═══ */}
      {pendientes.length > 0 && (
        <div className="card" style={{marginBottom:16, border:'1px solid #F0D48A', background:'#FFFBF0'}}>
          <div style={{fontWeight:700, fontSize:14, color:'#8A6100', marginBottom:4}}>
            ⏰ {pendientes.length} caja{pendientes.length > 1 ? 's' : ''} de un día anterior sin cerrar
          </div>
          <div style={{fontSize:12, color:'var(--color-muted)', marginBottom:12}}>
            No se cierran solas. Si anotaste ventas a mano (ej: por un corte de luz), regístralas primero
            en el POS apuntando a esa caja, y recién ahí ciérrala con el conteo real.
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {pendientes.map(p => (
              <div key={p.id} style={{border:'1px solid var(--color-border)', borderRadius:8, background:'white', overflow:'hidden'}}>
                <div style={{display:'flex', alignItems:'center', gap:12, padding:'10px 14px', flexWrap:'wrap'}}>
                  <div style={{flex:1, minWidth:180}}>
                    <div style={{fontWeight:600, fontSize:13}}>{p.caja_nombre} · {fmtFecha(p.apertura_en)}</div>
                    <div style={{fontSize:12, color:'var(--color-muted)'}}>Abrió {p.cajero} a las {fmtHora(p.apertura_en)} con {fmt(p.monto_inicial)}</div>
                  </div>
                  <button className="btn btn-secondary" style={{padding:'6px 12px', fontSize:12}}
                    onClick={()=>venderEnPendiente(p)}>🛒 Vender en esta caja</button>
                  <button className="btn btn-primary" style={{padding:'6px 12px', fontSize:12}}
                    onClick={()=>abrirCierrePendiente(p)}>
                    {pendienteAbierto === p.id ? 'Cancelar' : '🔒 Cerrar esta caja'}
                  </button>
                </div>

                {pendienteAbierto === p.id && (
                  <div style={{padding:'12px 14px', borderTop:'1px solid var(--color-border)', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10}}>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Efectivo contado</label>
                      <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                        value={formPend.efectivo} onChange={e=>setFormPend({...formPend, efectivo: e.target.value.replace(/[^0-9.,]/g,'')})}/>
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Débito</label>
                      <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                        value={formPend.debito} onChange={e=>setFormPend({...formPend, debito: e.target.value.replace(/[^0-9.,]/g,'')})}/>
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Crédito</label>
                      <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                        value={formPend.credito} onChange={e=>setFormPend({...formPend, credito: e.target.value.replace(/[^0-9.,]/g,'')})}/>
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Transferencias</label>
                      <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                        value={formPend.transferencia} onChange={e=>setFormPend({...formPend, transferencia: e.target.value.replace(/[^0-9.,]/g,'')})}/>
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Fondo para mañana</label>
                      <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                        value={formPend.fondo} onChange={e=>setFormPend({...formPend, fondo: e.target.value.replace(/[^0-9.,]/g,'')})}/>
                    </div>
                    <div style={{display:'flex', alignItems:'flex-end'}}>
                      <button className="btn btn-primary btn-block" disabled={guardandoPend}
                        onClick={()=>cerrarPendiente(p)}>
                        {guardandoPend ? 'Guardando...' : 'Confirmar cierre'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:'grid', gridTemplateColumns: esMobil ? '1fr' : '170px 1fr', gap:20, alignItems:'start'}}>

        <div style={{
          display:'flex', flexDirection: esMobil ? 'row' : 'column', gap:10,
          overflowX: esMobil ? 'auto' : 'visible', WebkitOverflowScrolling:'touch',
          paddingBottom: esMobil ? 4 : 0,
        }}>
          <NavBtn id="apertura">Abrir caja</NavBtn>
          <NavBtn id="movimientos" disabled={!abierta}>Movimientos</NavBtn>
          <NavBtn id="cierre" disabled={!abierta}>Cierre de caja</NavBtn>
          <NavBtn id="historial">Historial</NavBtn>
        </div>

        <div>

          {/* ═══ APERTURA ═══ */}
          {tab === 'apertura' && (
            <div style={{display:'grid', gridTemplateColumns: esMobil ? '1fr' : '1fr 300px', gap:20, alignItems:'start'}}>
              <div className="card">
                <div className="section-title">Saldo de apertura · Inicio de caja</div>
                {abierta ? (
                  <div style={{padding:'20px 0', textAlign:'center', color:'var(--color-muted)'}}>
                    La caja ya está abierta con {fmt(saldoInicial)}.<br/>Ciérrala para abrir una nueva.
                  </div>
                ) : (
                  <>
                    {cajas.length > 1 && (
                      <div className="form-group">
                        <label className="form-label">Caja</label>
                        <select className="form-input form-select" value={cajaId} onChange={e=>setCajaId(e.target.value)}>
                          {cajas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Monto</label>
                      <input className="form-input" type="text" inputMode="numeric" placeholder="ej: 20.000"
                        value={montoInicial} onChange={e=>setMontoInicial(e.target.value.replace(/[^0-9.,]/g,''))}
                        style={{fontSize:18, fontWeight:600}}/>
                      {fondoSugerido > 0 && (
                        <div style={{marginTop:8, fontSize:12, color:'var(--color-muted)'}}>
                          Ayer se dejó <strong style={{color:'var(--color-primary)', cursor:'pointer'}}
                            onClick={()=>setMontoInicial(String(fondoSugerido))}>{fmt(fondoSugerido)}</strong> para la apertura. Toca para usarlo.
                        </div>
                      )}
                      <div style={{display:'flex', gap:6, flexWrap:'wrap', marginTop:10}}>
                        {MONTOS_RAPIDOS.map(m => (
                          <button key={m} onClick={()=>setMontoInicial(String(m))} style={{
                            padding:'6px 12px', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer',
                            border:'1px solid var(--color-border-md)',
                            background: montoInicial===String(m) ? 'var(--color-primary)' : 'var(--color-bg)',
                            color: montoInicial===String(m) ? 'white' : 'var(--color-text)',
                          }}>{fmt(m)}</button>
                        ))}
                      </div>
                    </div>
                    <button className="btn btn-primary btn-lg btn-block" onClick={abrir} style={{marginTop:8}}>Abrir caja</button>
                  </>
                )}
              </div>
              <div className="card"><PanelRegistro/></div>
            </div>
          )}

          {/* ═══ MOVIMIENTOS ═══ */}
          {tab === 'movimientos' && abierta && (
            <div style={{display:'grid', gridTemplateColumns: esMobil ? '1fr' : '1fr 300px', gap:20, alignItems:'start'}}>
              <div className="card">
                <div className="section-title">Tipo de movimiento</div>
                <div style={{display:'flex', gap:6, marginBottom:14}}>
                  <button className={`pay-btn ${movForm.tipo==='ingreso'?'active':''}`}
                    onClick={()=>{ setMovForm({tipo:'ingreso',concepto:'',monto:movForm.monto}); setMotivoCustom(false) }}>
                    ↓ Ingreso de efectivo
                  </button>
                  <button className={`pay-btn ${movForm.tipo==='egreso'?'active':''}`}
                    onClick={()=>{ setMovForm({tipo:'egreso',concepto:'',monto:movForm.monto}); setMotivoCustom(false) }}>
                    ↑ Salida de efectivo
                  </button>
                </div>
                <div className="form-group">
                  <label className="form-label">Monto</label>
                  <input className="form-input" type="text" inputMode="numeric" placeholder="ej: 20.000"
                    value={movForm.monto} onChange={e=>setMovForm({...movForm,monto:e.target.value.replace(/[^0-9.,]/g,'')})}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Motivo</label>
                  <div style={{display:'flex', gap:6, flexWrap:'wrap', marginBottom:8}}>
                    {(movForm.tipo==='ingreso' ? MOTIVOS_INGRESO : MOTIVOS_EGRESO).map(m => (
                      <button key={m} onClick={()=>{
                        if (m==='Otro') { setMotivoCustom(true); setMovForm({...movForm,concepto:''}) }
                        else { setMotivoCustom(false); setMovForm({...movForm,concepto:m}) }
                      }} style={{
                        padding:'5px 10px', borderRadius:6, fontSize:12, fontWeight:500, cursor:'pointer',
                        border:'1px solid var(--color-border-md)',
                        background: movForm.concepto===m ? 'var(--color-primary)' : 'var(--color-bg)',
                        color: movForm.concepto===m ? 'white' : 'var(--color-text)',
                      }}>{m}</button>
                    ))}
                  </div>
                  {(motivoCustom || (movForm.concepto && !MOTIVOS_INGRESO.concat(MOTIVOS_EGRESO).includes(movForm.concepto))) && (
                    <input className="form-input" placeholder="ej: INGRESO DE MONEDAS PARA VUELTO"
                      value={movForm.concepto} onChange={e=>setMovForm({...movForm,concepto:e.target.value})}/>
                  )}
                </div>
                <button className="btn btn-secondary btn-block" onClick={registrarMov}>Registrar</button>
              </div>
              <div className="card"><PanelRegistro/></div>
            </div>
          )}

          {/* ═══ CIERRE ═══ */}
          {tab === 'cierre' && abierta && !cerrado && (
            <div style={{maxWidth:560}}>
              <div className="card">
                <div style={{fontSize:12, color:'var(--color-muted)', marginBottom:4}}>
                  Caja del {hoyStr()} · {turno.cajero || usuario?.nombre}
                </div>
                <div className="section-title" style={{marginBottom:14}}>Cierre de caja</div>

                {/* Fondo para mañana */}
                <div className="form-group">
                  <label className="form-label">1 · Efectivo para la siguiente apertura</label>
                  <input className="form-input" type="text" inputMode="numeric" placeholder="ej: 30.000"
                    value={fondoManana} onChange={e=>setFondoManana(e.target.value.replace(/[^0-9.,]/g,''))}/>
                  <div style={{fontSize:11, color:'var(--color-muted)', marginTop:4}}>
                    Dinero que apartas para abrir mañana. Sácalo del cajón antes de contar.
                  </div>
                </div>

                {/* Débito y crédito */}
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                  <div className="form-group" style={{marginBottom:0}}>
                    <label className="form-label">2 · Débito (según máquina)</label>
                    <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                      value={debitoReal} onChange={e=>setDebitoReal(e.target.value.replace(/[^0-9.,]/g,''))}/>
                  </div>
                  <div className="form-group" style={{marginBottom:0}}>
                    <label className="form-label">3 · Crédito (según máquina)</label>
                    <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                      value={creditoReal} onChange={e=>setCreditoReal(e.target.value.replace(/[^0-9.,]/g,''))}/>
                  </div>
                </div>
                <div style={{fontSize:11, color:'var(--color-muted)', margin:'6px 0 12px'}}>
                  Copia los montos del papelito del reporte de la máquina.
                </div>

                {/* Transferencia */}
                <div className="form-group">
                  <label className="form-label">4 · Transferencias recibidas</label>
                  <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                    value={transferenciaReal} onChange={e=>setTransferenciaReal(e.target.value.replace(/[^0-9.,]/g,''))}/>
                  <div style={{fontSize:11, color:'var(--color-muted)', marginTop:4}}>
                    Total de transferencias del turno, según tu app del banco.
                  </div>
                </div>

                {/* Efectivo contado */}
                <div className="form-group">
                  <label className="form-label">5 · Efectivo contado en el cajón</label>
                  <input className="form-input" type="text" inputMode="numeric" placeholder="$0"
                    value={efectivoContado} onChange={e=>setEfectivoContado(e.target.value.replace(/[^0-9.,]/g,''))}
                    style={{fontSize:20, fontWeight:700, textAlign:'center'}}/>
                  <div style={{fontSize:11, color:'var(--color-muted)', marginTop:4}}>
                    Cuenta el efectivo que quedó (ya sin el dinero de mañana).
                  </div>
                </div>

                {/* ARQUEO — solo admin ve el teórico y la diferencia */}
                {verArqueo && contado !== null && (
                  <div style={{marginBottom:14, padding:'12px 14px', borderRadius:8, background:'var(--color-bg)'}}>
                    <div style={{fontSize:11, color:'var(--color-muted)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8}}>Arqueo (solo admin)</div>
                    <div style={{fontSize:13, display:'flex', justifyContent:'space-between', marginBottom:4}}>
                      <span>Efectivo teórico</span><span style={{fontWeight:600}}>{fmt(efectivoTeorico)}</span>
                    </div>
                    <div style={{fontSize:13, display:'flex', justifyContent:'space-between', marginBottom:8}}>
                      <span>Efectivo contado</span><span style={{fontWeight:600}}>{fmt(contado)}</span>
                    </div>
                    <div style={{padding:'8px 10px', borderRadius:6, textAlign:'center', fontWeight:600, fontSize:14,
                      background: difEfectivo<0?'var(--color-danger-bg)':'var(--color-success-bg)',
                      color: colorDif(difEfectivo)}}>
                      {difEfectivo===0 ? '✓ Cuadrada' : difEfectivo<0 ? `Falta -${fmt(Math.abs(difEfectivo))}` : `Sobra +${fmt(difEfectivo)}`}
                    </div>
                  </div>
                )}

                <button className="btn btn-primary btn-lg btn-block" onClick={cerrar} style={{padding:14}}>
                  🔒 Confirmar cierre
                </button>
              </div>
            </div>
          )}

          {/* Confirmación de cierre a ciegas (vendedor) */}
          {tab === 'cierre' && cerrado && (
            <div style={{maxWidth:560}}>
              <div className="card" style={{textAlign:'center', padding:'40px 24px'}}>
                <div style={{fontSize:40}}>✓</div>
                <div style={{fontSize:18, fontWeight:600, color:'var(--color-success)', marginTop:8}}>Cierre registrado</div>
                <div style={{fontSize:13, color:'var(--color-muted)', marginTop:4}}>El turno quedó guardado correctamente. ¡Buen trabajo!</div>
              </div>
            </div>
          )}

          {/* ═══ HISTORIAL ═══ */}
          {tab === 'historial' && (
            <div className="card">
              <div style={{display:'flex', alignItems:'flex-end', gap:12, marginBottom:16}}>
                <div>
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-input" value={fechaHist}
                    max={new Date().toLocaleDateString('en-CA')}
                    onChange={e=>setFechaHist(e.target.value)} style={{width:200}}/>
                </div>
                <button className="btn btn-secondary" onClick={cargarHistorial}>Actualizar</button>
              </div>
              {cargandoHist ? <div className="page-loading" style={{height:100}}><div className="spinner"/></div> : (
                <>
                  <div className="metric-grid" style={{marginBottom:16}}>
                    <div className="metric-card">
                      <div className="metric-label">Total ventas</div>
                      <div className="metric-value" style={{color:'var(--color-success)'}}>{fmt(totalHist)}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Transacciones</div>
                      <div className="metric-value">{ventasHist.filter(v=>v.estado!=='anulado').length}</div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Anuladas</div>
                      <div className="metric-value" style={{color:'var(--color-danger)'}}>{ventasHist.filter(v=>v.estado==='anulado').length}</div>
                    </div>
                  </div>
                  {ventasHist.length === 0 ? (
                    <div className="empty-state"><p>No hay ventas para esa fecha</p></div>
                  ) : (
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Hora</th><th>Boleta</th><th>Vendedor</th><th>Total</th><th>Estado</th><th></th></tr></thead>
                        <tbody>
                          {ventasHist.map(v => (
                            <tr key={v.id}>
                              <td style={{color:'var(--color-muted)',fontSize:12}}>{fmtHora(v.creado_en)}</td>
                              <td style={{fontWeight:500}}>#{v.folio || v.id.slice(-6).toUpperCase()}</td>
                              <td style={{color:'var(--color-muted)'}}>{v.vendedor || '—'}</td>
                              <td style={{fontWeight:600}}>{fmt(v.total)}</td>
                              <td><span className={`badge ${v.estado==='anulado'?'badge-danger':'badge-success'}`}>{v.estado==='anulado'?'Anulada':'Emitida'}</span></td>
                              <td style={{textAlign:'right'}}>
                                {v.estado !== 'anulado' && (
                                  <button className="btn btn-secondary" style={{padding:'5px 10px', fontSize:12}}
                                    disabled={cargandoReimp === v.id} onClick={()=>reimprimir(v)}>
                                    {cargandoReimp === v.id ? '...' : '🖨️ Reimprimir'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ══ MODAL REIMPRESIÓN — Boleta / documento ══ */}
      {reimpresion && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.6)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:2000, padding:20
        }}>
          <div className="card" style={{
            width:'100%', maxWidth:420, maxHeight:'92vh',
            display:'flex', flexDirection:'column', padding:0, overflow:'hidden'
          }}>
            <div style={{
              padding:'16px 20px', borderBottom:'1px solid var(--color-border)',
              display:'flex', justifyContent:'space-between', alignItems:'center'
            }}>
              <div>
                <div style={{fontWeight:700, fontSize:15}}>🖨️ Reimprimir documento</div>
                <div style={{fontSize:12, color:'var(--color-muted)'}}>
                  Boleta #{reimpresion.documento.folio || reimpresion.documento.id?.slice(-6).toUpperCase()}
                </div>
              </div>
              <button onClick={()=>setReimpresion(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'var(--color-muted)'}}>✕</button>
            </div>

            <div style={{flex:1, overflowY:'auto', padding:'16px', background:'#f0f0ee'}}>
              <div style={{background:'white', boxShadow:'0 2px 8px rgba(0,0,0,.1)', display:'inline-block', width:'100%'}}>
                <Boleta venta={reimpresion} usuario={usuario}/>
              </div>
            </div>

            <div style={{padding:'16px 20px', borderTop:'1px solid var(--color-border)', display:'flex', flexDirection:'column', gap:8}}>
              <button className="btn btn-primary btn-block" onClick={imprimirBoleta}>
                🖨️ Imprimir boleta
              </button>
              <button className="btn btn-secondary btn-block" onClick={descargarPdfReimp} disabled={generandoPdf}>
                {generandoPdf ? 'Generando PDF...' : '📥 Descargar PDF'}
              </button>
              <button className="btn btn-secondary btn-block" onClick={()=>setReimpresion(null)} style={{color:'var(--color-muted)'}}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
